import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  finalize,
  map,
  shareReplay,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { PaginatedListOfGetSmsTemplateGroupsDto } from '../../../core/api/models';
import { SmsTemplateGroupsService } from '../../../core/api/services';
import { EventTypes } from '../../../core/constants/event-types';
import { AppDomService } from '../../../core/services/dom.service';

import { MatDialogModule } from '@angular/material/dialog';
import { PageNames } from 'src/app/configs/page-names.enum';
import { BaseTreeTablePageComponent } from '../../shared/base/components/base-tree-table-page.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { SmsTemplateComponent } from '../sms-template/sms-template.component';
import { GetSmsTemplateGroupsDto } from './../../../core/api/models/get-sms-template-groups-dto';
import { SmsTemplatesService } from './../../../core/api/services/sms-templates.service';
import { SmsTemplateGroupFormComponent } from './sms-template-group-form/sms-template-group-form.component';

@Component({
  selector: 'app-sms-template-group',
  templateUrl: './sms-template-group.component.html',
  styleUrls: [
    '../../shared/base/components/base-tree-table-page.components.scss',
  ],
  imports: [BaseIndexModule, MatDialogModule, SmsTemplateComponent],
})
export class SmsTemplateGroupComponent
  extends BaseTreeTablePageComponent
  implements OnInit
{
  //#region Properties

  pageName = PageNames.smsTemplateGroup;
  protected currentService = inject(SmsTemplateGroupsService);
  paginationApiMethodName: string =
    this.currentService.smsTemplateGroupsGetSmsTemplateGroupsWithPagination
      .name;
  deleteApiMethodName: string =
    this.currentService.smsTemplateGroupsDelete.name;
  createGetApiMethodName: string =
    this.currentService.smsTemplateGroupsCreateGet.name;
  auditApiMethodName: string =
    this.currentService.smsTemplateGroupsGetSmsTemplateGroupsAudit.name;
  modelWithPagination$: Observable<PaginatedListOfGetSmsTemplateGroupsDto>;
  items$: Observable<Array<GetSmsTemplateGroupsDto>>;
  items: treeModel[] = []; // use in html

  selectedItem: GetSmsTemplateGroupsDto;
  isVisibleSmsTemplateAuditPage: boolean = false;
  querySelectorPrefix: string = 'smsTG-tree-item-';

  selectedSmsTemplateGroupId: number;

  @Input('isShowAuditBtn') input_isShowAuditBtn: boolean = true;
  @Input('isModalMode') input_isModalMode: boolean = false;
  @Output('selectedSmsTemplate') output_selectedSmsTemplate =
    new EventEmitter<number>();

  //#endregion

  constructor(public smsTemplatesService: SmsTemplatesService) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.paginationModel.pageSize = 200;
      this.pageConfig.showAuditBtn = this.input_isShowAuditBtn;
      // this.pageConfig.showLoadingBar = !this.input_isModalMode;

      if (this.input_isModalMode) {
        this.pageConfig.showHelpBtn = false;
        this.pageConfig.tableConfig.preloadingRowCount = 0;
        // this.pageConfig.showTopHeaderBorderBottom = false;
        // this.pageConfig.showHeader=false;
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  configTableColumns(): void {
    this.tableColumns = [
      { title: this.localizer.title, name: 'title', hasSearch: true },
    ];
  }

  getTableData() {
    try {
      super.getTableData();
      this.modelWithPagination$ = this.getPaginationApi().pipe(
        shareReplay(),
        catchError((error) => {
          this.pageConfig.isEmpty = false;
          this.pageConfig.hasError = true;
          return error;
        })
      );

      this.getItems();
      this.setTreeItems(this.items$);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  onHideAuditPage() {
    this.showParent = true;
    this.getParentQueryString();
    this.pageConfig.isVisibleAuditPage = false;
    this.isVisibleSmsTemplateAuditPage = false;
  }

  emitSelectedSmsTemplate(selectedSmsTemplate) {
    this.output_selectedSmsTemplate.emit(selectedSmsTemplate);
  }

  //#region Api methods

  //Get ById
  baseGetByIdApi(id: number): void {
    this.selectedSmsTemplateGroupId = id;

    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;

    this.currentService
      .smsTemplateGroupsUpdateGet({ SmsTemplateGroupId: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseGetByIdObserverFormModal);
  }

  getSmsTemplateGroupByParentIdApi(
    parentId: number,
    subItems: treeModel[],
    nodeBtnElement?: any
  ) {
    if (subItems?.length) {
      this.onClickNodeBtn(nodeBtnElement, parentId);
      return;
    }

    //enable loading for fetch subItems from api
    this.changeNodeBtnLoading(nodeBtnElement, parentId, true);

    this.currentService
      .smsTemplateGroupsGetSmsTemplateGroupByParentId({ ParentId: parentId })
      .pipe(
        takeUntil(this.onDestroy$),
        map((items) => {
          // convert to tree items
          return items.data.map((item) => {
            return { subItems: [], ...item };
          });
        }),
        concatMap((phonebooks: treeModel[]) => {
          //find item and change subItems
          return this.changeParentSubItems(
            parentId,
            phonebooks,
            nodeBtnElement
          );
        }),
        tap((items: treeModel[]) => {
          //set new items and apply changes
          this.items = items;
        }),
        delay(1500),
        finalize(() => {
          // disable btn loading
          this.changeNodeBtnLoading(nodeBtnElement, parentId, false);
        })
      )
      .subscribe((items: treeModel[]) => {
        // expand node for display sub items
        this.onClickNodeBtn(nodeBtnElement, parentId);
      });
  }

  //#endregion

  //#region actions

  openCreateFormModal() {
    try {
      this.unSubs();

      let data = {
        isEdit: false,
        title: `${this.formPageConfig?.vocabs}addTitle`,
        parentId: this.selectedItem?.id,
      };

      const modalResult = this.dialogService.openModal({
        component: SmsTemplateGroupFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.pageConfig.isVisibleForm = undefined)),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  openEditFormModal(model: GetSmsTemplateGroupsDto) {
    try {
      this.unSubs();

      model.id = this.selectedSmsTemplateGroupId;

      let data = {
        title: `${this.formPageConfig?.vocabs}editTitle`,
        isEdit: true,
        model: model,
      };

      const modalResult = this.dialogService.openModal({
        component: SmsTemplateGroupFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  onDelete(model) {
    try {
      let dialogResult = this.dialogService.confirmDialog({
        content: model.title,
        isConfirmDelete: true,
        confirmByWritingText: true,
      });
      dialogResult.afterClosed().subscribe((result) => {
        if (result) {
          if (this.selectedItem?.id == model.id) this.selectedItem = {};
          this.baseDeleteApi(model.id);
        }
      });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  //#endregion

  //#region tree items

  setTreeItems(items$: Observable<GetSmsTemplateGroupsDto[]>) {
    try {
      items$
        .pipe(
          takeUntil(this.onDestroy$),
          map((items) => {
            return items.map((item) => {
              return { subItems: [], ...item };
            });
          }),
          tap((items) => {
            this.selectedItem = { ...items[0] };
          })
        )
        .subscribe((items) => {
          this.items = items;
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  getTreeItemsCount(model: treeModel[]) {
    try {
      if (!model.length) return;
      this.pageConfig.tableConfig.paginationModel.totalCount += model.length;

      model.forEach((item) => {
        if (item.subItems.length) this.getTreeItemsCount(item.subItems);
      });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  onSelect(model: treeModel) {
    try {
      //remove old active element
      const treeElements = document.querySelectorAll(
        `.${this.querySelectorPrefix}container [data-id]`
      );

      treeElements.forEach((element) => {
        if (element.classList.contains('row-active')) {
          element.classList.remove('row-active');
        }
      });

      //active element now
      let treeItem = document.getElementById(
        this.querySelectorPrefix + model.id
      );

      treeItem.classList.add('row-active');

      this.selectedItem = {};
      this.selectedItem = {
        id: model.id,
        parentId: model.parentId,
        title: model.title,
      };
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  //#endregion

  trackByItems(index: number, element: treeModel) {
    return element.id;
  }

  onUpdate(id?, setUrl = true) {
    this.baseGetByIdApi(id);
  }
}

export interface treeModel extends GetSmsTemplateGroupsDto {
  subItems: treeModel[];
}
